import { TIMEFRAMES, timeframeShort, type Timeframe } from '@pipyar/core';
import { cn } from '@/lib/utils';

interface TimeframeButtonsProps {
  value: Timeframe;
  onChange: (tf: Timeframe) => void;
  /** Tighter spacing for the full-screen overlay, where room is scarcer. */
  compact?: boolean;
  /**
   * Which timeframes to offer. Defaults to all of them.
   *
   * A module that cannot answer for a timeframe should not offer it: the knots
   * module skips M1, and showing the button anyway would present a dead end as
   * a choice.
   */
  options?: readonly Timeframe[];
}

/**
 * The timeframe selector, shared by every chart page.
 *
 * Was duplicated verbatim across all four modules, which also meant each page's
 * full-screen overlay would have needed its own copy. One component keeps the
 * page toolbar and the overlay driving the same state and looking the same.
 */
export function TimeframeButtons({
  value,
  onChange,
  compact = false,
  options = TIMEFRAMES,
}: TimeframeButtonsProps) {
  return (
    <>
      {options.map((tf) => (
        <button
          key={tf}
          type="button"
          onClick={() => onChange(tf)}
          aria-current={value === tf ? 'true' : undefined}
          className={cn(
            'rounded-lg font-medium transition-colors',
            compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm',
            value === tf
              ? 'bg-primary text-primary-foreground'
              : 'bg-elevated text-muted-foreground hover:text-foreground',
          )}
        >
          {timeframeShort(tf)}
        </button>
      ))}
    </>
  );
}
